/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
import GlobalModel from 'echarts/lib/model/Global';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import WordCloudSeries, {
  WordCloudDataItemOption,
  WordCloudDataValue,
  type WordCloudSingleLayoutOption
} from '../WordCloudSeries';
import {
  getExtendCoord,
  formatPaddingOrMargin,
  linePolygonIntersection,
  calculateOverlapArea,
  isRectangleInsidePolygon
} from './helper';
import getPointsFromSeries from '../layoutHelper/getPointsFromSeries';
import { Line, Point, Rect } from '../type';

type DataFormat = {
  coord: number;
  width: number;
  height: number;
  weight: number;
  value?: WordCloudDataValue;
  index?: number;
  [key: string]: any;
};

export default function themeRiverLayout(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachSeriesByType('wordCloud', function (wordCloudSeries: WordCloudSeries) {
    const coordSys = wordCloudSeries.coordinateSystem;
    if (!coordSys || coordSys.type !== 'single') {
      return;
    }
    const layoutConfig = wordCloudSeries.get('layout') as WordCloudSingleLayoutOption;
    const { mask } = layoutConfig;

    // step0 获取多边形点集
    const points = getPointsFromSeries(mask, ecModel, api, wordCloudSeries);

    // step1 获取用于计算的数据
    const axis = coordSys.getAxis();
    const seriesData = wordCloudSeries.getData();
    const wordCloudData = seriesData.hostModel.option.data as WordCloudDataItemOption[];
    let dataFormat: DataFormat[] = wordCloudData.map((d, i) => {
      const { value, symbolSize, itemStyle } = d;
      const { margin } = itemStyle;
      const marginFormat = formatPaddingOrMargin(margin);
      const axisValue = value[0] as number;
      const text = value[1] as string;
      const weight = value[2] as number;
      const coord = axis.toGlobalCoord(axis.dataToCoord(axisValue));
      const width = (symbolSize[0] + marginFormat[1] + marginFormat[3]) as number;
      const height = (symbolSize[1] + marginFormat[0] + marginFormat[2]) as number;
      return {
        value,
        coord,
        width,
        height,
        weight,
        index: i,
        text
      } as DataFormat;
    });

    // step2 筛选数据，超出画布的不参与布局
    // 获取扩展的coord范围
    const isHor = axis.orient === 'horizontal';
    const extendCoordRange: [number, number] = getExtendCoord(axis);
    dataFormat = dataFormat.filter(d => {
      const { coord, width, height } = d;
      const graphicSpan = [coord + (isHor ? width : height), coord - (isHor ? width : height)];
      return graphicSpan[0] >= extendCoordRange[0] && graphicSpan[1] <= extendCoordRange[1];
    });

    // step3 计算布局
    doSingleLayout(
      points,
      dataFormat,
      { ...layoutConfig, isHor, extendCoordRange },
      wordCloudSeries
    );
  });
}

function doSingleLayout(
  points: Point[],
  dataFormat: DataFormat[],
  layoutConfig: WordCloudSingleLayoutOption & {
    isHor: boolean;
    extendCoordRange: [number, number];
  },
  wordCloudSeries: WordCloudSeries
) {
  const {
    isHor,
    fuzzyWidth,
    extendCoordRange,
    edgeAlign,
    overlapAreaThreshold,
    precision,
    aggregation
  } = layoutConfig;

  // 按照权重排序，权重可能相等，所以还需要额外的排序元素，此处指定为字符串排序
  dataFormat.sort((a, b) => {
    // 根据 weight 排序
    if (a.weight !== b.weight) {
      return b.weight - a.weight;
    }
    // 根据 text 长度排序
    if (a.text.length !== b.text.length) {
      return a.text.length - b.text.length;
    }
    // 根据 text 字符排序
    return a.text.localeCompare(b.text);
  });

  const seriesData = wordCloudSeries.getData();
  // 记录已经布局的Rect
  const layoutRecord: Rect[] = [];
  // 记录single正交方向的分割线信息
  const singleLines = getSingleOrthogonalSplitLines(dataFormat, points, isHor);
  dataFormat.forEach(d => {
    const { coord, index } = d;
    const singleLine = singleLines[coord];
    if (!singleLine) {
      return;
    }
    let getPosition = false;

    const primaryAxisCoords = getPrimaryAxisCoords(d, extendCoordRange, {
      isHor,
      edgeAlign,
      fuzzyWidth,
      precision
    });

    const secondAxisCoords = getSecondAxisCoords(d, singleLine, precision);

    // 往画布中心聚拢的鱼骨布局
    for (
      let primaryAxisStep = 0;
      primaryAxisStep < primaryAxisCoords.length;
      primaryAxisStep += 1
    ) {
      const primaryAxisCurrent = primaryAxisCoords[primaryAxisStep];

      for (
        let secondaryAxisStep = 0;
        secondaryAxisStep < secondAxisCoords.length;
        secondaryAxisStep += 1
      ) {
        const secondaryAxisCurrent = secondAxisCoords[secondaryAxisStep];

        const point: Point = isHor
          ? [primaryAxisCurrent, secondaryAxisCurrent]
          : [secondaryAxisCurrent, primaryAxisCurrent];
        const result = tryPutRectOnPoint(point, d, points, layoutRecord, {
          overlapAreaThreshold,
          aggregation,
          isHor
        });
        if (result) {
          seriesData.setItemLayout(index, [result.x, result.y]);
          getPosition = true;
          break;
        }
      }
      if (getPosition) {
        break;
      }
    }

    // 未获取到位置，设置为NaN
    if (!getPosition) {
      seriesData.setItemLayout(index, [NaN, NaN]);
    }
  });
}

// 获取轴线正交方向的分割线信息
function getSingleOrthogonalSplitLines(data: DataFormat[], points: Point[], isHor: boolean) {
  const result: Record<number, { min: number; max: number; mid: number }> = {};
  if (!points.length || !data.length) {
    return result;
  }

  data.forEach(d => {
    const { coord } = d;

    let intersections = points.filter(p => {
      return isHor ? p[0] === coord : p[1] === coord;
    });

    if (!intersections.length) {
      intersections = linePolygonIntersection(coord, points);
    }

    const values = intersections.map(p => {
      return isHor ? p[1] : p[0];
    });

    const min = Math.min(...values);
    const max = Math.max(...values);
    const mid = (min + max) / 2;

    result[coord] = {
      min,
      max,
      mid
    };
  });
  return result;
}

// 获取单个词在主轴上的布局范围和顺序
function getPrimaryAxisCoords(
  data: DataFormat,
  extendCoordRange: [number, number],
  config: {
    isHor: boolean;
    edgeAlign: boolean | number;
    fuzzyWidth: number;
    precision: number;
  }
) {
  const { coord, width, height, weight } = data;
  const { isHor, edgeAlign, fuzzyWidth, precision } = config;
  let result: number[] = [];
  // 计算中线坐标
  const coordMid = (extendCoordRange[0] + extendCoordRange[1]) / 2;

  /**
   * 为了保持布局更加合理，当fuzzyWidth不为0时
   * 主轴方向上，左侧的数据会优先向右侧布局（如果主轴是Y轴，则上侧的数据优先向下次下侧布局）
   */
  // 1代表沿主轴方向，-1代表反方向
  let primaryAxisDirection = coord < coordMid ? 1 : -1;
  const originAxisDirection = primaryAxisDirection;
  let primaryAxisCurrent = coord;
  let primaryAxisCount = 0;

  // 统计fuzzyWidth范围
  for (
    let primaryAxisStep = 0;
    primaryAxisStep < fuzzyWidth * 2 + precision;
    primaryAxisStep += precision
  ) {
    primaryAxisCurrent = coord + primaryAxisCount * primaryAxisDirection;

    result.push(primaryAxisCurrent);

    primaryAxisCount += precision;
    if (primaryAxisCount >= fuzzyWidth) {
      primaryAxisCount = 0;
      primaryAxisDirection *= -1;
    }
  }

  const canEdgeAlign = typeof edgeAlign === 'number' ? weight >= edgeAlign : edgeAlign;
  if (canEdgeAlign) {
    // 计算data边缘对齐时的coord
    const size = isHor ? width : height;
    // 手动申明NumberEPSILON，以解决PIP问题中的边界问题
    // 如果使用外部的PIP边界计算函数，会带来严重的性能问题
    const numberEpsilon = 0.01;
    const coordEdge =
      originAxisDirection === 1
        ? extendCoordRange[0] + size / 2 + numberEpsilon
        : extendCoordRange[1] - size / 2 - numberEpsilon;

    // 统计是否有导致图形超出边缘的坐标，如果有，才会进行吸边操作
    const isOverEdge = result.some(r => {
      const graphicEdge = originAxisDirection === 1 ? r - size / 2 : r + size / 2;
      return originAxisDirection === 1 ? graphicEdge <= coordEdge : graphicEdge >= coordEdge;
    });

    if (!isOverEdge) {
      return result;
    }
    // 删除fuzzyWidth中超出边缘的坐标
    result = result.filter(r => {
      return originAxisDirection === 1 ? r >= coordEdge : r <= coordEdge;
    });

    // 新增边缘对齐的坐标
    result.push(coordEdge);
  }

  return result;
}

// 获取单个词在副轴上的布局范围和顺序
function getSecondAxisCoords(
  data: DataFormat,
  singleLine: { min: number; max: number; mid: number },
  precision: number
) {
  const { min, max, mid } = singleLine;

  const mid2minRange: number[] = [];
  // fillRange
  for (let pointer = mid - precision; pointer >= min; pointer -= precision) {
    mid2minRange.push(pointer);
  }

  const mid2maxRange: number[] = [];
  // fillRange
  for (let pointer = mid; pointer <= max; pointer += precision) {
    mid2minRange.push(pointer);
  }

  const maxLength = Math.max(mid2minRange.length, mid2maxRange.length);
  const result = [mid];
  for (let i = 0; i < maxLength; i += 1) {
    const mid2min = mid2minRange[i];
    const mid2max = mid2maxRange[i];

    if (mid2min) {
      result.push(mid2min);
    }
    if (mid2max) {
      result.push(mid2max);
    }
  }

  return result;
}

// 计算与已经布局的矩形的面积重叠的平均值
function getOverlapAreaAvg(rect: Rect, layoutRecord: Rect[]) {
  let overlapAreaSum = 0;
  let overlapCount = 0;
  layoutRecord.forEach(d => {
    const area = calculateOverlapArea(d, rect);
    if (area > 0) {
      overlapAreaSum += area;
      overlapCount += 1;
    }
  });

  return overlapCount === 0 ? 0 : overlapAreaSum / overlapCount;
}

// 矩形是否在多边形内
function isInPolygon(rect: Rect, polygon: Point[]) {
  return isRectangleInsidePolygon(rect, polygon, 3);
}

// 矩形是可显示，即未被聚合
function isHideByOverlap(
  rect: Rect,
  layoutRecord: Rect[],
  config: {
    aggregation: boolean | number;
    isHor: boolean;
  }
) {
  const { aggregation, isHor } = config;
  if (!aggregation) {
    return false;
  }
  const { text } = rect;
  const sameTextRects = layoutRecord.filter(d => d.text === text);
  const aggregationTemp = typeof aggregation === 'number' ? aggregation : 0.3;
  const textCoordExtend: Line = isHor
    ? {
        start: [rect.x, 0],
        end: [rect.x + rect.width, 0]
      }
    : {
        start: [0, rect.y],
        end: [0, rect.y + rect.height]
      };
  const textRectLength = isHor ? rect.width : rect.height;

  return sameTextRects.some(d => {
    const rectCoordExtend: Line = isHor
      ? {
          start: [d.x, 0],
          end: [d.x + d.width, 0]
        }
      : {
          start: [0, d.y],
          end: [0, d.y + d.height]
        };

    const overlapStart = Math.max(
      rectCoordExtend.start[isHor ? 0 : 1],
      textCoordExtend.start[isHor ? 0 : 1]
    );
    const overlapEnd = Math.min(
      rectCoordExtend.end[isHor ? 0 : 1],
      textCoordExtend.end[isHor ? 0 : 1]
    );

    const overlapLength = overlapEnd - overlapStart < 0 ? 0 : overlapEnd - overlapStart;
    const overlapRatio = overlapLength / textRectLength;

    return overlapRatio >= aggregationTemp;
  });
}

// 尝试将矩形放置在点上
function tryPutRectOnPoint(
  point: Point,
  rect: DataFormat,
  points: Point[],
  layoutRecord: Rect[],
  config: {
    overlapAreaThreshold: number;
    aggregation: boolean | number;
    isHor: boolean;
  }
) {
  const { aggregation, overlapAreaThreshold, isHor } = config;
  const { width, height, value } = rect;

  const rectTemp = {
    x: point[0] - width / 2,
    y: point[1] - height / 2,
    width,
    height,
    text: value[value.length - 2]
  };

  const result = {
    x: point[0],
    y: point[1]
  };

  // 与已经布局的矩形是否冲突
  const overlapAreaAvg = getOverlapAreaAvg(rectTemp, layoutRecord);
  const isOverlapWithRect = overlapAreaAvg > overlapAreaThreshold;
  // 是否在多边形内
  const isPolygonContain = isInPolygon(rectTemp, points);
  // 是否因聚合被隐藏
  const isHide = isHideByOverlap(rectTemp, layoutRecord, { aggregation, isHor });

  const canPut = !isOverlapWithRect && isPolygonContain && !isHide;
  if (canPut) {
    layoutRecord.push(rectTemp);
  }
  return canPut && result;
}
